import { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Input, Popover } from "antd";
import { DownOutlined, SearchOutlined } from "@ant-design/icons";
import type { DesignScopeListItem } from "mock-data/design-scope";

export type ClassificationFilterOption = {
  id: string;
  code: string;
  title: string;
  count: number;
};

export type ClassificationFilterGroup = {
  key: string;
  label: string;
  options: ClassificationFilterOption[];
};

export type ClassificationFilterModel = {
  groups: ClassificationFilterGroup[];
  total: number;
};

const EMPTY_FILTER: ClassificationFilterModel = { groups: [], total: 0 };

function compareCode(a: string, b: string) {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

function groupLabel(code: string, title: string) {
  const name = title.trim();
  if (!name) return code;
  return `${code} - ${name.toUpperCase()}`;
}

function uniformatOf(item: DesignScopeListItem) {
  const hit = item.classifications.find((row) => row.system === "uni-format");
  if (!hit) return null;
  const code = (hit.number ?? "").trim();
  const title = (hit.title ?? "").trim();
  if (!code && !title) return null;
  const root = hit.path[0];
  const groupCode = (root?.number ?? code.charAt(0) ?? "").trim() || "—";
  const groupTitle = root?.number ? (root.title ?? "") : "";
  return {
    id: code || title,
    code,
    title,
    groupKey: groupCode,
    groupLabel: groupLabel(groupCode, groupTitle)
  };
}

/** Distinct UniFormat codes on these scope items, grouped under the root path. */
export function buildUniformatFilter(
  items: DesignScopeListItem[]
): ClassificationFilterModel {
  const groups = new Map<
    string,
    { label: string; options: Map<string, ClassificationFilterOption> }
  >();
  let total = 0;
  for (const item of items) {
    const hit = uniformatOf(item);
    if (!hit) continue;
    total += 1;
    let group = groups.get(hit.groupKey);
    if (!group) {
      group = { label: hit.groupLabel, options: new Map() };
      groups.set(hit.groupKey, group);
    }
    const existing = group.options.get(hit.id);
    if (existing) existing.count += 1;
    else {
      group.options.set(hit.id, {
        id: hit.id,
        code: hit.code,
        title: hit.title,
        count: 1
      });
    }
  }
  return {
    total,
    groups: [...groups.entries()]
      .sort(([a], [b]) => compareCode(a, b))
      .map(([key, group]) => ({
        key,
        label: group.label,
        options: [...group.options.values()].sort((a, b) =>
          compareCode(a.code || a.title, b.code || b.title)
        )
      }))
  };
}

function masterformatDivision(item: DesignScopeListItem) {
  const hit = item.classifications.find(
    (row) => row.system === "master-format"
  );
  if (!hit) return null;
  const root = hit.path[0];
  const code = (
    root?.number ||
    (hit.number ?? "").trim().split(/\s+/)[0] ||
    ""
  ).trim();
  if (!code) return null;
  const title = root?.number ? (root.title ?? "").trim() : "";
  return { id: code, code, title };
}

/** MasterFormat divisions present on these scope items, with an item count.
 *  `sectionLabel` names the section; pass "" where the options need no heading. */
export function buildMasterformatFilter(
  items: DesignScopeListItem[],
  sectionLabel = "DIVISIONS IN THIS MILESTONE"
): ClassificationFilterModel {
  const options = new Map<string, ClassificationFilterOption>();
  let total = 0;
  for (const item of items) {
    const hit = masterformatDivision(item);
    if (!hit) continue;
    total += 1;
    const existing = options.get(hit.id);
    if (existing) existing.count += 1;
    else options.set(hit.id, { ...hit, count: 1 });
  }
  if (!options.size) return EMPTY_FILTER;
  return {
    total,
    groups: [
      {
        key: "divisions",
        label: sectionLabel,
        options: [...options.values()].sort((a, b) =>
          compareCode(a.code, b.code)
        )
      }
    ]
  };
}

export function masterformatDivisionId(item: DesignScopeListItem): string {
  return masterformatDivision(item)?.id ?? "";
}

function optionIds(model: ClassificationFilterModel) {
  return model.groups.flatMap((group) =>
    group.options.map((option) => option.id)
  );
}

function selectedWithOption(
  selected: string[],
  optionId: string,
  checked: boolean
): string[] {
  if (!checked) return [...selected, optionId];
  return selected.filter((id) => id !== optionId);
}

function ClassificationFilterPanel({
  testId,
  allLabel,
  searchPlaceholder,
  model,
  draft,
  showOptionCounts,
  chipClass,
  open,
  onDraft,
  onApply,
  onClear
}: {
  testId: string;
  allLabel: string;
  searchPlaceholder: string;
  model: ClassificationFilterModel;
  draft: string[];
  showOptionCounts: boolean;
  chipClass: string;
  open: boolean;
  onDraft: (next: string[]) => void;
  onApply: () => void;
  onClear: () => void;
}) {
  const [search, setSearch] = useState("");
  useEffect(() => {
    if (open) setSearch("");
  }, [open]);
  const ids = optionIds(model);
  const selected = draft.filter((id) => ids.includes(id));
  const allChecked = ids.length > 0 && selected.length === ids.length;
  const query = search.trim().toLowerCase();
  const groups = useMemo(
    () =>
      model.groups
        .map((group) => ({
          ...group,
          options: group.options.filter((option) => {
            if (!query) return true;
            return `${option.code} ${option.title} ${group.label}`
              .toLowerCase()
              .includes(query);
          })
        }))
        .filter((group) => group.options.length > 0),
    [model.groups, query]
  );

  return (
    <div className="w-[340px]" data-testid={`${testId}-panel`}>
      <div className="border-0 border-b border-solid border-black/10 p-2">
        <Input
          allowClear
          prefix={<SearchOutlined className="text-black/25" />}
          placeholder={searchPlaceholder}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          data-testid={`${testId}-search`}
        />
      </div>
      <div className="max-h-[280px] overflow-y-auto py-1">
        <label className="flex cursor-pointer items-center gap-2 px-3 py-1.5">
          <Checkbox
            checked={allChecked}
            indeterminate={selected.length > 0 && !allChecked}
            disabled={ids.length === 0}
            onChange={() => onDraft(allChecked ? [] : ids)}
            data-testid={`${testId}-all`}
          />
          <span className="min-w-0 flex-1 truncate text-sm">{allLabel}</span>
          <span className="text-sm text-black/45">{model.total}</span>
        </label>
        {groups.map((group) => (
          <div key={group.key}>
            {group.label ? (
              <div className="px-3 pb-1 pt-2 text-xs font-medium tracking-wide text-black/45">
                {group.label}
              </div>
            ) : null}
            {group.options.map((option) => {
              const checked = selected.includes(option.id);
              return (
                <label
                  key={option.id}
                  data-testid={`${testId}-option-${option.id}`}
                  className={`mx-1 flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 ${
                    checked ? "bg-ciq-blue-50" : ""
                  }`}
                >
                  <Checkbox
                    checked={checked}
                    onChange={() =>
                      onDraft(selectedWithOption(selected, option.id, checked))
                    }
                  />
                  {option.code ? (
                    <span
                      className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${chipClass}`}
                    >
                      {option.code}
                    </span>
                  ) : null}
                  <span className="min-w-0 flex-1 truncate text-sm">
                    {option.title}
                  </span>
                  {showOptionCounts ? (
                    <span className="text-sm text-black/45">
                      {option.count}
                    </span>
                  ) : null}
                </label>
              );
            })}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 border-0 border-t border-solid border-black/10 px-3 py-2">
        <span className="text-sm text-black/45">
          {selected.length} selected
        </span>
        <button
          type="button"
          className="ml-auto border-0 bg-transparent p-0 text-sm text-black/65"
          data-testid={`${testId}-clear`}
          onClick={onClear}
        >
          Clear
        </button>
        <Button
          type="primary"
          size="small"
          data-testid={`${testId}-apply`}
          onClick={onApply}
        >
          Apply
        </Button>
      </div>
    </div>
  );
}

export function ClassificationFilterMenu({
  testId,
  placeholder,
  allLabel,
  searchPlaceholder,
  model,
  value,
  onChange,
  showOptionCounts = false,
  chipClass
}: {
  testId: string;
  placeholder: string;
  allLabel: string;
  searchPlaceholder: string;
  model: ClassificationFilterModel;
  value: string[];
  onChange: (next: string[]) => void;
  showOptionCounts?: boolean;
  chipClass: string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(value);
  const options = model.groups.flatMap((group) => group.options);

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const label = useMemo(() => {
    if (!value.length) return placeholder;
    if (value.length === 1) {
      const hit = options.find((option) => option.id === value[0]);
      if (!hit) return "1 selected";
      return [hit.code, hit.title].filter(Boolean).join(" ");
    }
    return `${value.length} selected`;
  }, [options, placeholder, value]);

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      open={open}
      onOpenChange={setOpen}
      styles={{ container: { padding: 0 } }}
      content={
        <ClassificationFilterPanel
          testId={testId}
          allLabel={allLabel}
          searchPlaceholder={searchPlaceholder}
          model={model}
          draft={draft}
          showOptionCounts={showOptionCounts}
          chipClass={chipClass}
          open={open}
          onDraft={setDraft}
          onClear={() => setDraft([])}
          onApply={() => {
            const ids = new Set(optionIds(model));
            onChange(draft.filter((id) => ids.has(id)));
            setOpen(false);
          }}
        />
      }
    >
      <button
        type="button"
        data-testid={testId}
        className="flex h-8 w-52 items-center justify-between gap-2 rounded border border-solid border-black/15 bg-white px-3 text-left text-sm"
      >
        <span
          className={`min-w-0 flex-1 truncate ${value.length ? "" : "text-black/25"}`}
        >
          {label}
        </span>
        <DownOutlined className="text-xs text-black/45" />
      </button>
    </Popover>
  );
}
